"use client"

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react"
import { signOut, useSession } from "next-auth/react"
import { IdleWarningModal } from "./IdleWarningModal"
import {
  canTakeLock, combineDraftResults, DRAFT_WAIT_MS, idleState, shouldPing,
  type DraftResult, type IdleState, type LogoutLock,
} from "@/lib/session/idle"
import { idleStore } from "@/lib/session/idle-store"

// Le suivi de l'activite (CNX) : la fenetre « Toujours la ? » a 14 minutes, la deconnexion a 15, dans tous les
// onglets a la fois. La derniere activite se partage entre onglets par localStorage (relu chaque seconde), et par
// un BroadcastChannel quand localStorage manque ; un seul onglet (celui du verrou) termine la session, puis tous
// vont a la page de connexion. Avant la fin de la session, chaque onglet garde son travail non enregistre (CNX-E03).

const ACTIVITY_KEY = "af:last-activity"
const LOCK_KEY = "af:idle-logout"
const CHANNEL = "af-session"
const EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const

type Message =
  | { type: "activity"; at: number }
  | { type: "idle-logout" }
  | { type: "draft-result"; result: DraftResult }
  | { type: "logged-out"; url: string }

function readNumber(key: string): number {
  try { return Number(localStorage.getItem(key)) || 0 } catch { return 0 }
}
function readLock(): LogoutLock {
  try { return JSON.parse(localStorage.getItem(LOCK_KEY) || "null") } catch { return null }
}
function write(key: string, value: string | null) {
  try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value) } catch { /* stockage indisponible */ }
}

export function ActivityTracker() {
  const { status } = useSession()
  // Une fois connecte, le suivi reste monte : deconnecte depuis un autre onglet, celui-ci doit encore partir
  const [seen, setSeen] = useState(false)
  useEffect(() => { if (status === "authenticated") setSeen(true) }, [status])
  if (status !== "authenticated" && !seen) return null
  return <Tracker signedOut={status === "unauthenticated"} />
}

function Tracker({ signedOut }: { signedOut: boolean }) {
  const tabId = useRef("")
  const shared = useRef(0)
  const local = useRef(0)
  const lastPing = useRef(0)
  const lastWrite = useRef(0)
  const loggingOut = useRef(false)
  const channel = useRef<BroadcastChannel | null>(null)
  const [state, setState] = useState<IdleState>({ show: "none", secondsLeft: 60 })
  const backgroundJob = useSyncExternalStore(idleStore.subscribe, idleStore.getBackgroundJob, () => false)
  const editor = useSyncExternalStore(idleStore.subscribe, idleStore.getEditor, () => null)
  const draftResults = useRef<DraftResult[]>([])
  const draftKept = useRef<Promise<DraftResult> | null>(null)

  const post = (m: Message) => { try { channel.current?.postMessage(m) } catch { /* canal ferme */ } }

  const markActivity = useCallback(() => {
    const now = Date.now()
    local.current = now
    shared.current = now
    // Ecrire et diffuser au plus une fois par seconde : pointermove arrive en rafale
    if (now - lastWrite.current >= 1000) {
      lastWrite.current = now
      write(ACTIVITY_KEY, String(now))
      post({ type: "activity", at: now })
    }
  }, [])

  // Partir sans la question du navigateur : le travail vient d'etre garde, ou la session est deja finie
  const leave = useCallback((url: string) => {
    idleStore.getEditor()?.allowLeave()
    window.location.href = url
  }, [])

  /** Garder le travail non enregistre de cet onglet, s'il en a ; une seule fois, quel que soit l'ordre des messages */
  const keepDraft = useCallback((): Promise<DraftResult> => {
    if (!draftKept.current) {
      const handle = idleStore.getEditor()
      draftKept.current = handle ? handle.keepDraft().catch(() => "none" as const) : Promise.resolve("none" as const)
    }
    return draftKept.current
  }, [])

  /**
   * La session est finie cote serveur (ou ailleurs) : garder le travail de cet onglet (le serveur refusant, il reste
   * dans le navigateur), effacer le cookie (sinon la page de connexion renvoie a /), puis partir.
   */
  const expire = useCallback(async (url = "/login?expired=1") => {
    if (loggingOut.current) return
    loggingOut.current = true
    const kept = combineDraftResults([await keepDraft()])
    await signOut({ redirect: false }).catch(() => undefined)
    leave(kept ? `${url}${url.includes("?") ? "&" : "?"}travail=${kept}` : url)
  }, [leave, keepDraft])


  const logout = useCallback(async (expired: boolean) => {
    if (loggingOut.current) return
    loggingOut.current = true
    let url = expired ? "/login?expired=1" : "/login?raison=inactivite"
    const now = Date.now()
    if (!canTakeLock(readLock(), tabId.current, now)) {
      // Un autre onglet mene la deconnexion : garder mon travail et le lui dire ; s'il ne previent pas de la fin
      // (canal indisponible), partir quand meme
      void keepDraft().then((result) => post({ type: "draft-result", result }))
      setTimeout(() => leave(url), 10_000)
      return
    }
    write(LOCK_KEY, JSON.stringify({ owner: tabId.current, at: now }))
    // Chaque onglet garde son travail pendant que la session vit encore ; on attend leurs reponses, 2 s au plus
    draftResults.current = []
    post({ type: "idle-logout" })
    // Mon travail et le delai laisse aux autres onglets, en meme temps (keepDraft a son propre delai maximal)
    const [own] = await Promise.all([keepDraft(), new Promise((r) => setTimeout(r, DRAFT_WAIT_MS))])
    const kept = expired ? null : combineDraftResults([own, ...draftResults.current])
    if (kept) url += `&travail=${kept}`
    await fetch("/api/session/end", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: "IDLE" }),
    }).catch(() => undefined)
    await signOut({ redirect: false }).catch(() => undefined)
    post({ type: "logged-out", url })
    write(LOCK_KEY, null)
    leave(url)
  }, [leave, keepDraft])

  useEffect(() => {
    tabId.current = crypto.randomUUID()
    // Ouvrir ou recharger une page est une activite, signalee tout de suite au serveur (lastPing a zero)
    markActivity()
    lastPing.current = 0

    if (typeof BroadcastChannel !== "undefined") {
      channel.current = new BroadcastChannel(CHANNEL)
      channel.current.onmessage = (e: MessageEvent<Message>) => {
        const m = e.data
        if (m.type === "activity") shared.current = Math.max(shared.current, m.at)
        else if (m.type === "logged-out") leave(m.url)
        else if (m.type === "draft-result") draftResults.current.push(m.result)
        else if (m.type === "idle-logout" && !draftKept.current) {
          // Un autre onglet mene la deconnexion : garder mon travail et le lui dire. Cet onglet garde ses propres
          // replis (sa minuterie, le depart apres la deconnexion) si l'autre disparait en route.
          void keepDraft().then((result) => post({ type: "draft-result", result }))
        }
      }
    }
    for (const ev of EVENTS) window.addEventListener(ev, markActivity, { capture: true, passive: true })

    const timer = setInterval(() => {
      if (loggingOut.current) return
      const now = Date.now()
      shared.current = Math.max(shared.current, readNumber(ACTIVITY_KEY))
      const next = idleState(now, shared.current)
      setState((prev) => (prev.show === next.show && prev.secondsLeft === next.secondsLeft ? prev : next))

      if (next.show === "logout" || next.show === "expired") {
        void logout(next.show === "expired")
        return
      }
      if (shouldPing({ now, localActivity: local.current, lastPing: lastPing.current })) {
        lastPing.current = now
        fetch("/api/session/activity", { method: "POST" })
          .then((res) => { if (res.status === 401) void expire() })
          .catch(() => undefined)
      }
    }, 1000)

    return () => {
      clearInterval(timer)
      for (const ev of EVENTS) window.removeEventListener(ev, markActivity, { capture: true })
      channel.current?.close()
      channel.current = null
    }
  }, [markActivity, logout, leave, expire, keepDraft])

  // Deconnecte ailleurs (un autre onglet, « Se deconnecter ») : le message « logged-out » donne l'adresse ; s'il
  // n'arrive pas, la page de connexion, apres avoir garde le travail de cet onglet
  useEffect(() => {
    if (!signedOut || loggingOut.current) return
    const t = setTimeout(() => void expire("/login"), 2000)
    return () => clearTimeout(t)
  }, [signedOut, expire])

  if (state.show !== "warning" || signedOut) return null
  return (
    <IdleWarningModal
      secondsLeft={state.secondsLeft}
      backgroundJob={backgroundJob}
      editor={editor ? { projectName: editor.projectName, unsaved: editor.unsaved } : null}
      onStay={markActivity}
      onSaveAndStay={async () => { markActivity(); await editor?.save().catch(() => undefined) }}
      onLogout={() => { loggingOut.current = true; void signOut({ callbackUrl: "/login" }) }}
    />
  )
}
